var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import * as Styled from "./family-cascade-drawer-button.style";
import { ComponentSelect } from "./component-select";
import { ComponentSection } from "./component-section";
import { drawerTransitionDelay, narrowDrawerWidth, } from "ui/components/elements/custom-drawer/custom-drawer.style";
import * as DrawerStyled from "ui/components/elements/custom-drawer/custom-drawer.style";
import { useCloseOnOutsidePointer } from "hooks/use-close-on-outside-pointer";
import { PressureGaugeIcon } from "ui/components/elements/icons/pressure-gauge";
import { actions, selectors, useAppDispatch, useAppSelector } from "store";
export var cupFamilyDrawerId = "cup-family-drawer";
export var pumpFamilyDrawerId = "pump-family-drawer";
/* The drawers lie over the canvas, and PlayCanvas listens on `window` for any
 * press within the canvas bounds, so presses and scrolls on them are kept from
 * reaching the scene — as the in-scene pickers do. */
var stopScenePointer = function (event) { return event.stopPropagation(); };
/**
 * The two-step sidebar picker: a family drawer, and a second panel with the
 * family's items that slides out beside it once a family is picked. Used for
 * suction cups (family → size) and vacuum pumps (family → model). While the
 * options carry no families at all — pumps until Tacton sends the family
 * field — the drawer skips the family step and lists every item directly.
 *
 * Unlike the other sidebar drawers it does not freeze the scene while open:
 * it opens by itself for a new node, and the user should be able to drag that
 * node into place straight away instead of first spending a click on closing
 * the drawer.
 */
export var FamilyCascadeDrawerButton = function (_a) {
    var id = _a.id, sidebarWidth = _a.sidebarWidth, header = _a.header, value = _a.value, stacked = _a.stacked, shouldHighlight = _a.shouldHighlight, familyHeader = _a.familyHeader, itemHeader = _a.itemHeader, itemDetailHeader = _a.itemDetailHeader, familyOptions = _a.familyOptions, familyImages = _a.familyImages, selectedFamilies = _a.selectedFamilies, onSelectFamily = _a.onSelectFamily, itemOptions = _a.itemOptions, selectedItemValues = _a.selectedItemValues, onSelectItem = _a.onSelectItem, itemPanelContent = _a.itemPanelContent, itemPanelWidth = _a.itemPanelWidth, familyFooter = _a.familyFooter;
    var dispatch = useAppDispatch();
    var activeSidebarItemID = useAppSelector(selectors.selectActiveSidebarItemID);
    var isDrawerActive = activeSidebarItemID === id;
    var hasFamilies = familyOptions.length > 0;
    var _b = __read(useState(false), 2), isItemPanelOpen = _b[0], setIsItemPanelOpen = _b[1];
    var _c = __read(useState(false), 2), isDrawerMounted = _c[0], setIsDrawerMounted = _c[1];
    var _d = __read(useState(false), 2), isItemPanelMounted = _d[0], setIsItemPanelMounted = _d[1];
    var drawerContentRef = useRef(null);
    var itemPanelRef = useRef(null);
    // Reset the item panel when the family drawer closes
    useEffect(function () {
        if (!isDrawerActive) {
            setIsItemPanelOpen(false);
        }
    }, [isDrawerActive]);
    // Delayed mount/unmount for family drawer content
    useEffect(function () {
        if (isDrawerActive) {
            setIsDrawerMounted(true);
            return;
        }
        var timeout = setTimeout(function () { return setIsDrawerMounted(false); }, drawerTransitionDelay);
        return function () { return clearTimeout(timeout); };
    }, [isDrawerActive]);
    // Delayed mount/unmount for the item cascade panel
    useEffect(function () {
        if (isItemPanelOpen) {
            setIsItemPanelMounted(true);
            return;
        }
        var timeout = setTimeout(function () { return setIsItemPanelMounted(false); }, drawerTransitionDelay);
        return function () { return clearTimeout(timeout); };
    }, [isItemPanelOpen]);
    // Close the drawer when the user clicks outside both the family drawer and
    // the item cascade panel. A text-selection drag that starts inside either
    // and is released outside is not treated as an outside click.
    useCloseOnOutsidePointer(isDrawerActive, isItemPanelOpen ? [drawerContentRef, itemPanelRef] : [drawerContentRef], function () { return dispatch(actions.setActiveSidebarItemID()); });
    var handleSelectFamily = function (family) {
        onSelectFamily(family);
        setIsItemPanelOpen(true);
    };
    var toggleDrawer = function (e) {
        e.stopPropagation();
        if (!isDrawerActive) {
            dispatch(actions.setActiveSidebarItemID(id));
            if (selectedFamilies.length > 0) {
                setIsItemPanelOpen(true);
            }
        }
        else {
            dispatch(actions.setActiveSidebarItemID(undefined));
        }
    };
    var itemList = function (surface) { return (_jsx(ComponentSelect, { labels: itemOptions.map(function (o) { return o.label; }), values: itemOptions.map(function (o) { return o.value; }), images: itemOptions.map(function (o) { return o.image; }), compact: true, details: itemOptions.map(function (o) { return o.detail; }), groups: itemOptions.map(function (o) { return o.group; }), selectedValues: selectedItemValues, onValueChange: onSelectItem, scrollIntoPosition: "nearest", surface: surface })); };
    var itemPanelHeader = (_jsxs(Styled.DrawerHeader, { children: [_jsx("span", { children: itemHeader }), itemDetailHeader && (_jsxs(Styled.DrawerHeaderDetail, { children: [_jsx(PressureGaugeIcon, { size: "1em" }), itemDetailHeader] }))] }));
    return (_jsxs(Styled.Root, { children: [_jsxs(DrawerStyled.SidebarDrawerRoot, { isVisible: isDrawerActive, sidebarWidth: sidebarWidth, drawerWidth: narrowDrawerWidth, flushContent: true, children: [_jsx(DrawerStyled.SidebarButtonContainer, { children: _jsx(ComponentSection, { header: header, selected: isDrawerActive, onClick: toggleDrawer, value: value, stacked: stacked, shouldHighlight: shouldHighlight }) }), _jsx(DrawerStyled.SidebarDrawerContentContainer, { ref: drawerContentRef, onMouseDown: stopScenePointer, onWheel: stopScenePointer, children: isDrawerMounted && (_jsxs(Styled.FamilyDrawerContentRoot, { children: [hasFamilies ? (_jsxs(_Fragment, { children: [_jsx(Styled.DrawerHeader, { children: familyHeader }), _jsx(ComponentSelect, { labels: familyOptions, values: familyOptions, images: familyImages, selectedValues: selectedFamilies, onValueChange: handleSelectFamily, scrollIntoPosition: "nearest" })] })) : (_jsxs(_Fragment, { children: [itemPanelHeader, itemList()] })), familyFooter && (_jsx(Styled.FamilyFooter, { children: familyFooter }))] })) })] }), hasFamilies && (_jsx(Styled.ItemPanel, { ref: itemPanelRef, isVisible: isItemPanelOpen, sidebarWidth: sidebarWidth, drawerWidth: narrowDrawerWidth, panelWidth: itemPanelWidth, onMouseDown: stopScenePointer, onWheel: stopScenePointer, children: isItemPanelMounted && (_jsx(DrawerStyled.SidebarDrawerContentRoot, { children: itemPanelContent !== null && itemPanelContent !== void 0 ? itemPanelContent : (_jsxs(_Fragment, { children: [itemPanelHeader, itemList(Styled.itemPanelSurface)] })) })) }))] }));
};
